'use client';

import type { FC, ReactNode, RefObject } from 'react';
import { useEffect, useRef } from 'react';
import { cn } from '../utils/cn';
import { useScreenLayout } from './useScreenLayout';
import { useScrollRestoration } from '../hooks/useScrollRestoration';
import { useIsDesktop } from '../hooks/useIsDesktop';
import { LegalSection } from '../screens/HomeScreen/components/LegalSection';

interface ScreenContentProps {
  children: ReactNode;
  className?: string;
  resetScrollOnMount?: boolean;
  scrollRef?: RefObject<HTMLDivElement | null>;
  scrollKey?: string;
  /** Opt out of the desktop legal footer (used by full-bleed screens like the case roulette). */
  hideLegalSection?: boolean;
}

export const ScreenContent: FC<ScreenContentProps> = ({
  children,
  className,
  resetScrollOnMount = true,
  scrollRef: externalScrollRef,
  scrollKey,
  hideLegalSection = false,
}) => {
  const internalScrollRef = useRef<HTMLDivElement | null>(null);
  const scrollRef = externalScrollRef || internalScrollRef;
  const { disableSafeArea } = useScreenLayout();
  const isDesktop = useIsDesktop();

  useScrollRestoration({
    scrollKey: scrollKey || '',
    scrollRef,
    enabled: !!scrollKey,
  });

  useEffect(() => {
    if (!scrollKey && resetScrollOnMount && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [scrollKey, resetScrollOnMount, scrollRef]);

  // The previous implementation gated the whole scroll viewport on
  // `layoutReady` (visibility:hidden until ResizeObserver finished
  // measuring fixed-top/bottom on the client). That hid all SSR
  // content for one frame on every paint — visible flash on every
  // refresh and on every client navigation. Drop the gate; CSS
  // variables fall back to 0px until measurement, which means the
  // first paint may slightly under-pad against the fixed header,
  // but the layout settles within one ResizeObserver tick. Trade
  // 16ms of subpixel shift for the absence of the universal blank-
  // then-content flash.
  return (
    <div
      ref={scrollRef}
      data-screen-scroll
      className={cn(
        // overflow-anchor keeps the viewport pinned to a visible child when list content
        // above it grows/shrinks (rush-mid games appearing/disappearing) — no scroll jump.
        // Chrome/Android honor it natively; iOS WebKit does not (needs a JS anchor there).
        // Резерв снизу — это верх панели: её высота плюс её же подъём над низом окна
        // (`--screen-fixed-bottom-lift`, тот самый, по которому панель и стоит). Раньше
        // здесь было своё выражение `панель + таб-бар`, которое считало высоту панели и её
        // подъём взаимоисключающими, хотя они складываются, — и содержимое уезжало под панель.
        //
        // Слагаемые `--screen-tabbar-height` и безопасная зона в `max()` нужны для случая
        // БЕЗ панели: там `lift` равен нулю, и резерв обязан остаться прежним — таб-бар или
        // зона, что больше. С панелью `панель + lift` заведомо больше обоих.
        //
        // Третье слагаемое — `--screen-fixed-bottom-gap`: резерв «ровно до верха панели»
        // оставляет последнюю карточку приклеенной к ней, и экран читается как обрезанный.
        // Зазор тоже приходит переменной и тоже равен нулю без панели, поэтому в случае
        // «таб-бар без панели» это выражение по-прежнему сводится к прежнему числу.
        'absolute top-0 start-0 end-0 bottom-0 overflow-y-auto overflow-x-hidden [overflow-anchor:auto] [-webkit-overflow-scrolling:touch] pt-[max(var(--screen-fixed-top-height,0px),calc(var(--tg-viewport-content-safe-area-inset-top,0px)+var(--tg-viewport-safe-area-inset-top,0px)))] pb-[max(calc(var(--screen-fixed-bottom-height,0px)+var(--screen-fixed-bottom-lift,0px)+var(--screen-fixed-bottom-gap,0px)),var(--screen-tabbar-height,0px),calc(var(--tg-viewport-content-safe-area-inset-bottom,0px)+var(--tg-viewport-safe-area-inset-bottom,0px)))]',
        // Sticky legal footer: column-flex the scroll area so the legal block's
        // mt-auto pins it to the bottom of the viewport when content is short, and
        // lets it flow right after content once the screen actually scrolls.
        isDesktop && !hideLegalSection && 'flex flex-col',
        !isDesktop && 'bg-black',
        // То же выражение без слагаемого безопасной зоны: этот режим существует ровно для
        // того, чтобы содержимое доходило до нижней кромки, когда панели нет. Но панель, если
        // она есть, всё равно стоит на своём подъёме — и её высоту с подъёмом резервировать
        // обязательно, иначе на «Деталях кейса» цена последнего скина уезжает под панель.
        disableSafeArea && '!pt-[var(--screen-fixed-top-height,0px)] !pb-[max(calc(var(--screen-fixed-bottom-height,0px)+var(--screen-fixed-bottom-lift,0px)+var(--screen-fixed-bottom-gap,0px)),var(--screen-tabbar-height,0px))]',
        className
      )}
    >
      {children}
      {isDesktop && !hideLegalSection && (
        <div className="mt-auto">
          <LegalSection />
        </div>
      )}
    </div>
  );
};
